import { Box, Button, CardMedia, Typography } from '@material-ui/core';
import { useWeb3React } from '@web3-react/core';
import clsx from 'clsx';
import { BNB_TOKEN } from 'common';
import CommonInput from 'components/common/CommonInput';
import CommonSelectInput from 'components/common/CommonSelectInput';
import { convertThousandSeperator } from 'helpers';
import CloseIcon from 'icon/CloseIcon';
import MoneyBagIcon from 'icon/MoneyBagIcon';
import RefreshIcon from 'icon/RefreshIcon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateHostPredictionStateAction } from 'store/actions/hostPredictionActions';
import { getUserBalance } from 'store/actions/userActions';
import {
  getEventType,
  getOrganizingMethod,
  getUserState,
} from 'store/selectors';
import { EUnit, WhoTakeWith } from 'types/hostPrediction';
import ComponentWithTooltip from './ComponentWithTooltip';
import { useStyles } from './PrizePoolStyle';

const PrizePool = () => {
  const classes = useStyles();
  const organizingMethod = useSelector(getOrganizingMethod);
  const userState = useSelector(getUserState);
  const eventType = useSelector(getEventType);
  const [isRefresh, setIsRefresh] = useState(false);
  const { library, account } = useWeb3React();

  const dispatch = useDispatch();

  const [liquidityPoolError, setLiquidityPoolError] = useState({
    bnbLiquidityError: false,
    linkLiquidityError: false,
    xmetaLiquidityError: false,
    aheLiquidityError: false,
    efunLiquidityError: false,
  });

  const chains = useMemo(() => {
    return [
      {
        value: EUnit.EFUN,
        id: process.env.REACT_APP_EFUN_TOKEN || '',
        Icon: (
          <CardMedia image="/images/EfunCoin.png" className={classes.coin} />
        ),
      },
      {
        value: EUnit.BNB,
        id: BNB_TOKEN,
        Icon: (
          <CardMedia image="/images/BNBCoin.png" className={classes.coin} />
        ),
      },
      {
        value: EUnit.LINK,
        id: process.env.REACT_APP_LINK_TOKEN || '',
        Icon: (
          <CardMedia image="/images/LinkCoin.png" className={classes.coin} />
        ),
      },
      {
        value: EUnit.XMETA,
        id: process.env.REACT_APP_XMETA_TOKEN || '',
        Icon: <CardMedia image="/images/XMETA.png" className={classes.coin} />,
      },
      {
        value: EUnit.AHE,
        id: process.env.REACT_APP_AHE_TOKEN || '',
        Icon: <CardMedia image="/images/AHE.png" className={classes.coin} />,
      },
    ];
  }, [classes.coin]);

  const addNewToken = useCallback(() => {
    const tokenChoose = organizingMethod.betting.map((b) => b.token);
    const chainNotChoose = chains
      .map((c) => c.id)
      .filter((u) => !tokenChoose.includes(u));
    if (chainNotChoose.length == 0) return;
    dispatch(
      updateHostPredictionStateAction({
        organizingMethod: {
          ...organizingMethod,
          betting: [
            ...organizingMethod.betting,
            {
              token: chainNotChoose[0],
              liquidityPool: '',
            },
          ],
        },
      }),
    );
  }, [dispatch, organizingMethod, chains]);

  const renderChains = useCallback(
    (index: number) => {
      const selectedUnit = organizingMethod.betting
        .filter((b, i) => i != index && !!b.token)
        .map((b) => b.token);
      return chains
        .filter((c) => !selectedUnit.includes(c.id))
        .map((c) => ({
          id: c.id,
          value: c.value,
          Icon: c.Icon,
        }));
    },
    [chains, organizingMethod.betting],
  );

  const onRemoveOption = useCallback(
    (index: number) => {
      const newOptions = organizingMethod.betting;
      newOptions.splice(index, 1);
      dispatch(
        updateHostPredictionStateAction({
          organizingMethod: {
            ...organizingMethod,
            betting: newOptions,
          },
        }),
      );
    },
    [dispatch, organizingMethod.betting],
  );

  const handleChangeUnit = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = event.target.value;
      const name = event.target.name.split('-');
      const newOption: any = [...organizingMethod.betting];
      newOption[+name[1]][name[0]] = newValue;
      dispatch(
        updateHostPredictionStateAction({
          organizingMethod: {
            ...organizingMethod,
            betting: newOption,
          },
        }),
      );
    },
    [dispatch, organizingMethod],
  );

  const handleChangeLiquidityPool = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = event.target.value;
      if (newValue.includes(',')) {
        newValue = newValue.replace(/,/g, '');
      }
      const name = event.target.name.split('-');
      const newOption: any = [...organizingMethod.betting];
      newOption[+name[1]][name[0]] = newValue;
      dispatch(
        updateHostPredictionStateAction({
          organizingMethod: {
            ...organizingMethod,
            betting: newOption,
          },
        }),
      );
    },
    [dispatch, organizingMethod],
  );

  const renderError = useCallback(
    (index: number) => {
      const token = organizingMethod.betting[index].token;
      if (token == BNB_TOKEN) {
        return liquidityPoolError.bnbLiquidityError;
      }
      if (token == process.env.REACT_APP_EFUN_TOKEN) {
        return liquidityPoolError.efunLiquidityError;
      }
      if (token == process.env.REACT_APP_XMETA_TOKEN) {
        return liquidityPoolError.xmetaLiquidityError;
      }
      if (token == process.env.REACT_APP_AHE_TOKEN) {
        return liquidityPoolError.aheLiquidityError;
      }
      return liquidityPoolError.linkLiquidityError;
    },
    [organizingMethod.betting, liquidityPoolError],
  );

  const disabledAddOptions = useMemo(() => {
    return organizingMethod.betting.length >= chains.length;
  }, [organizingMethod.betting.length, chains.length]);

  const updateValidateLiquidityPool = useCallback(() => {
    const newLiquidityPoolError = {
      bnbLiquidityError: false,
      linkLiquidityError: false,
      xmetaLiquidityError: false,
      aheLiquidityError: false,
      efunLiquidityError: false,
    };
    organizingMethod.betting.forEach((b) => {
      if (b.token == BNB_TOKEN) {
        if (Number(b.liquidityPool || '0') > +userState.bnbBalance) {
          newLiquidityPoolError.bnbLiquidityError = true;
        } else if (Number(b.liquidityPool || '0') <= +userState.bnbBalance) {
          newLiquidityPoolError.bnbLiquidityError = false;
        }
      } else if (b.token == process.env.REACT_APP_EFUN_TOKEN) {
        if (Number(b.liquidityPool || '0') + 2000 > +userState.efunBalance) {
          newLiquidityPoolError.efunLiquidityError = true;
        } else if (
          Number(b.liquidityPool || '0') + 2000 <=
          +userState.efunBalance
        ) {
          newLiquidityPoolError.efunLiquidityError = false;
        }
      } else if (b.token == process.env.REACT_APP_LINK_TOKEN) {
        if (Number(b.liquidityPool || '0') > +userState.linkBalance) {
          newLiquidityPoolError.linkLiquidityError = true;
        } else if (Number(b.liquidityPool || '0') <= +userState.linkBalance) {
          newLiquidityPoolError.linkLiquidityError = false;
        }
      } else if (b.token == process.env.REACT_APP_XMETA_TOKEN) {
        if (Number(b.liquidityPool || '0') > +userState.xmetaBalance) {
          newLiquidityPoolError.xmetaLiquidityError = true;
        } else if (Number(b.liquidityPool || '0') <= +userState.xmetaBalance) {
          newLiquidityPoolError.xmetaLiquidityError = false;
        }
      } else if (b.token == process.env.REACT_APP_AHE_TOKEN) {
        if (Number(b.liquidityPool || '0') > +userState.aheBalance) {
          newLiquidityPoolError.aheLiquidityError = true;
        } else if (Number(b.liquidityPool || '0') <= +userState.aheBalance) {
          newLiquidityPoolError.aheLiquidityError = false;
        }
      }
    });
    setLiquidityPoolError(newLiquidityPoolError);
  }, [organizingMethod.betting, userState]);

  useEffect(() => {
    updateValidateLiquidityPool();
  }, [organizingMethod, userState]);

  useEffect(() => {
    let error = false;
    const testValidation = Object.values(liquidityPoolError).filter((l) => l);
    if (Object.values(liquidityPoolError).filter((v) => v).length > 0) {
      error = true;
    } else if (testValidation.length > 0) error = true;
    else {
      const test = organizingMethod.betting.filter(
        (b) => !Number(b.liquidityPool || '0') || !b.token,
      );
      error = test.length > 0;
    }
    dispatch(
      updateHostPredictionStateAction({
        errorPool: error,
      }),
    );
  }, [organizingMethod, liquidityPoolError]);
  const titlePoolAnnotate = () => {
    return (
      <Box>
        <Typography>
          You can add up to 3 prize pools to your event. If you want to offer
          your own token as prize, please contact the EFUN admin team via
          Telegram .
        </Typography>
        <Typography>
          What tokens user uses to predict, he will be rewarded with the same
          tokens from the corresponding prize pool
        </Typography>
      </Box>
    );
  };
  const titleLiquidityPoolAnnotate = () => {
    return (
      <Box>
        <Typography>
          The liquidity pool is used to pay for winners of the event.
        </Typography>
        <Typography>
          You can add up to 3 liquidity pools to your event. If you want to
          offer your own token as prize, please contact the EFUN admin team via
          Telegram .
        </Typography>
        <Typography>
          What tokens user uses to predict, he will be rewarded with the same
          tokens from the corresponding liquidity pool
        </Typography>
      </Box>
    );
  };

  const onRefreshBalance = useCallback(() => {
    if (!account) return;
    setIsRefresh(true);
    dispatch(
      getUserBalance(library.provider, account, () => {
        setIsRefresh(false);
      }),
    );
  }, [dispatch, library.provider, account]);

  return (
    <ComponentWithTooltip
      title={
        eventType === WhoTakeWith.USER_USER ? 'Prize Pool' : 'Liquidity pool'
      }
      isAnnotate={true}
      titleAnnotate={
        eventType === WhoTakeWith.USER_USER
          ? titlePoolAnnotate()
          : titleLiquidityPoolAnnotate()
      }
      className={classes.marginTop}
    >
      <Box className={classes.container}>
        <Box className={clsx(classes.svg, 'center-root')}>
          <MoneyBagIcon width={67} height={67} />
        </Box>
        <Box className={classes.main}>
          {organizingMethod.betting.map((b, i) => (
            <Box className={classes.wrapperPool} key={i}>
              <CommonInput
                value={convertThousandSeperator(b.liquidityPool || '', true)}
                onChange={handleChangeLiquidityPool}
                className={classes.liquidityPool}
                name={`liquidityPool-${i}`}
                placeholder="Enter your pool"
                error={renderError(i)}
                helperText={renderError(i) ? 'Insufficient balance' : ''}
                required={true}
              />
              <CommonSelectInput
                values={renderChains(i)}
                onChange={handleChangeUnit}
                currentValue={b.token}
                className={classes.selectCoin}
                label="Token selector"
                name={`token-${i}`}
              />
              {i != 0 && (
                <Button
                  disableRipple
                  className={classes.clear}
                  onClick={() => onRemoveOption(i)}
                >
                  <CloseIcon color="#FFFFFF" />
                </Button>
              )}
            </Box>
          ))}
          <Button
            disabled={disabledAddOptions}
            onClick={addNewToken}
            className={classes.addPoolBtn}
            classes={{ disabled: classes.disabled }}
          >
            Additional pool
          </Button>
        </Box>
        <Button
          disableRipple
          onClick={onRefreshBalance}
          className={clsx(classes.refreshBtn, {
            [classes.refreshAnimation]: isRefresh,
          })}
        >
          <RefreshIcon color="white" />
        </Button>
      </Box>
    </ComponentWithTooltip>
  );
};

export default PrizePool;
